import clsx from 'clsx';
import { mdiLoading } from '@mdi/js';
import Icon from '@mdi/react';
import qs from 'qs';
import React, { useCallback, useState, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import formatDate from 'date-fns/format';
import { useIntl } from 'react-intl';
import usePostAppointment from '../../hooks/usePostAppointment';
import { FreeSlot } from "../../types/FreeSlot";
import { SlotsByDate } from "../../hooks/useStoreSlots";
import getStoreSlots from "../../api/getStoreSlots";
import validateEmail, { EmailValidationResult } from '../../api/validateEmail';
import validateBirtdate from '../../utils/validateBirthdate'
import useSetPersonalInformation from '../../hooks/useSetPersonalInformation';
import useSetWizardStep from '../../hooks/useSetWizardStep';
import useWizardContext from '../../hooks/useWizardContext';
import { Gender } from '../../types/PersonalInformation';
import { WizardStep } from '../../types/Wizard';
import classes from './classes.module.css';

type Inputs = {
  agreementMarketing: boolean;
  appointmentOnBehalfOf: string;
  day: string;
  emailAddress: string;
  gender: Gender;
  houseNumber: string;
  lastName: string;
  month: string;
  name: string;
  note: string;
  phoneNumber: number | string;
  postcode: string;
  year: string;
};

const emailPattern = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/i;

//replaced with formatSelectedSlotDateOnly and formatSelectedSlotTimeOnly
// const formatSelectedSlot = (locale: string, d: Date) => {
//   const time = d.toLocaleTimeString(locale, {
//     hour: 'numeric',
//     hour12: false,
//     minute: '2-digit',
//   });

//   const day = d.toLocaleDateString(locale, {
//     weekday: 'long',
//   });

//   const date = d.toLocaleDateString(locale, {
//     day: 'numeric',
//     month: 'long',
//   });

//   return `${day}, ${date} ${time}`;
// };


const formatSelectedSlotDateOnly = (locale: string, d: Date) => {

  const day = d.toLocaleDateString(locale, {
    weekday: 'long',
  });

  const dayUppercase = day.charAt(0).toUpperCase() + day.slice(1);

  const date = d.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
  });

  return `${dayUppercase} ${date}`;
};

const formatSelectedSlotTimeOnly = (locale: string, d: Date) => {

  const time = d.toLocaleTimeString(locale, {
    hour: 'numeric',
    hour12: false,
    minute: '2-digit',
  });

  return `${time}`;
};

const PersonalDataInput: React.FC = () => {
  const ctx = useWizardContext();
  const [isSubmitted, setIsSubmitted] = React.useState<boolean>(false);
  const setStep = useSetWizardStep();
  const setPersonalInformation = useSetPersonalInformation();
  const intl = useIntl();
  const [emailValidationResult, setEmailValidationResult] = useState<EmailValidationResult | null>(null);
  const [postAppointmentResponse, regAppointment] = usePostAppointment();

  const savedData = {
    agreementMarketing: ctx.personalInformation?.agreementMarketing || false,
    appointmentOnBehalfOf: ctx.personalInformation ? ctx.personalInformation.appointmentOnBehalfOf : '',
    day: ctx.personalInformation
      ? `${ctx?.personalInformation?.dateOfBirth.substring(ctx?.personalInformation?.dateOfBirth.lastIndexOf('-') + 1)}`
      : '',
    emailAddress: ctx.personalInformation ? `${ctx?.personalInformation?.emailAddress}` : '',
      gender: (ctx.personalInformation && ctx.personalInformation.gender) || ('' as Gender),
      houseNumber: ctx.personalInformation ? `${ctx?.personalInformation?.houseNumber}` : '',
    lastName: ctx.personalInformation ? `${ctx?.personalInformation?.lastName}` : '',
    month: ctx.personalInformation
      ? `${ctx?.personalInformation?.dateOfBirth.substring(
          ctx?.personalInformation?.dateOfBirth.indexOf('-') + 1,
          ctx?.personalInformation?.dateOfBirth.lastIndexOf('-'),
        )}`
      : '',
    name: ctx.personalInformation ? `${ctx?.personalInformation?.firstName}` : '',
    note: ctx.personalInformation ? `${ctx?.personalInformation?.note}` : '',
      phoneNumber: ctx.personalInformation ? `${ctx?.personalInformation?.phoneNumber}` : '',
      postcode: ctx.personalInformation ? `${ctx?.personalInformation?.postcode}` : '',
    year: ctx.personalInformation
      ? `${ctx?.personalInformation?.dateOfBirth.substring(0, ctx?.personalInformation?.dateOfBirth.indexOf('-'))}`
      : '',
  };

  const {
    register,
    handleSubmit,
    setFocus,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<Inputs>({
    defaultValues: savedData,
    mode: 'onChange',
  });

  const [extraInfoOpen, setExtraInfoOpen] = useState(false);
  const [slotsByDate, setStoreSlots] = React.useState<SlotsByDate | null>(null);
  const [slotAvailability, setSlotAvailability] = useState(true);

  const appointmentTypeId = ctx.appointmentType?.id === '35f42b66-7450-4df4-9226-63a49d9cde78'
  ? ctx.store?.appointmentTypeId
  : ctx.appointmentType?.id;
  let limit = 90;
  if (appointmentTypeId == '9b0ff9f0-29bf-4653-9d23-e875346bbb9a') {
    limit = 14;
  }
  const shopNumber = ctx.store?.shopNumber;
  const stepMax = (window as any).maxStep ?? 5

  const reduceThis = (slots: FreeSlot[]) =>
  slots
    .sort((a, b) => new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime())
    .reduce<SlotsByDate>((acc, slot) => {
      const date = formatDate(new Date(slot.startDateTime), 'yyyy-MM-dd');
      acc[date] = acc[date] || [];
      acc[date].push(slot);
      return acc;
    }, {});

  useEffect(() => {
    if (isSubmitted){
      if (appointmentTypeId && shopNumber) {
      const { cancel } = getStoreSlots({ appointmentTypeId, limit, shopNumber })
        .then((res) => {
          setStoreSlots(res.success ? reduceThis(res.payload.slots) : null);
        })
        .catch(() => {
          setStoreSlots(null);
        })
      return cancel;
    } else {
      setStoreSlots(null);
    }
    }
  }, [isSubmitted])

  const checkIfSlotIsStillAvailable = () => {
    let returnValue = false;
    const storeSlot = ctx.storeSlot;
    const dateString = storeSlot?.startDateTime;
    if (dateString) {
      const date = formatDate(new Date(dateString), 'yyyy-MM-dd');
      if (slotsByDate) {
        const slots = slotsByDate[date];
        slots.forEach((slot) => {
          const d1 = new Date(slot.startDateTime);
          const d2 = new Date(dateString);
          if (d1.getTime() == d2.getTime()) {
            returnValue = true;
            return;
          }
        });
      }
    }
    return returnValue;
  };

  useEffect(() => {
    if (slotsByDate) {
      if (!checkIfSlotIsStillAvailable()) {
        setSlotAvailability(false);
        return;
      } else {
        const { utm_campaign, utm_content, utm_medium, utm_source, utm_term } = qs.parse(window.location?.search || '', {
      ignoreQueryPrefix: true,
    });

    const { utmCampaign, utmContent, utmMedium, utmSource, utmTerm } = JSON.parse(
      document.cookie.match('(^|;)\\s*utmCookie\\s*=\\s*([^;]+)')?.pop() || '{}',
    );

    const sanitizeUtm = (p: string | qs.ParsedQs | string[] | qs.ParsedQs[] | undefined) =>
      typeof p === 'string' ? p : undefined;

    //we now eventually take the appointment type id from the store (before, we always took it from the selection on first screen)
    //this is only the case if the user has chosen appointment type "Adviesgesprek (30 minuten)", which has appointment type id "35f42b66-7450-4df4-9226-63a49d9cde78"
    const appointmentTypeIdToBeSent =
      ctx.appointmentType?.id === '35f42b66-7450-4df4-9226-63a49d9cde78'
        ? ctx.store?.appointmentTypeId
        : ctx.appointmentType?.id;

    regAppointment({
      appointmentOnBehalfOf: ctx.personalInformation?.appointmentOnBehalfOf,
      appointmentTypeId: appointmentTypeIdToBeSent,
      dateOfBirth: ctx.personalInformation?.dateOfBirth,
      emailAddress: ctx.personalInformation?.emailAddress,
      endDateTime: ctx.storeSlot?.endDateTime,
      firstName: ctx.personalInformation?.firstName,
      gender: ctx.personalInformation?.gender,
      houseNumber: ctx.personalInformation?.houseNumber,
      lastName: ctx.personalInformation?.lastName,
      note: `Receive newsletters: ${ctx.personalInformation?.agreementMarketing}

      Customer note:
      ${ctx.personalInformation?.note}
            `,
      phoneNumber: ctx.personalInformation?.phoneNumber,
      postcode: ctx.personalInformation?.postcode,
      roomId: ctx.storeSlot?.roomId,
      shopNumber: ctx.store?.shopNumber,
      startDateTime: ctx.storeSlot?.startDateTime,
      utmCampaign: sanitizeUtm(utm_campaign || utmCampaign),
      utmContent: sanitizeUtm(utm_content || utmContent),
      utmMedium: sanitizeUtm(utm_medium || utmMedium),
      utmSource: sanitizeUtm(utm_source || utmSource),
      utmTerm: sanitizeUtm(utm_term || utmTerm),
    });
        return;
      }
    }
  }, [slotsByDate])

  const onSubmit: SubmitHandler<Inputs> = useCallback(
    (data) => {
      const goToNextStep = () => {
        setPersonalInformation({
          agreementMarketing: data.agreementMarketing,
          appointmentOnBehalfOf: `${data.appointmentOnBehalfOf}`,
          dateOfBirth: `${data.year}-${data.month.toString().length === 1 ? '0' : ''}${data.month}-${
            data.day.toString().length === 1 ? '0' : ''
          }${data.day}`,
          emailAddress: data.emailAddress,
          firstName: data.name,
          gender: data.gender,
          houseNumber: data.houseNumber,
          lastName: data.lastName,
          note: data.note,
            phoneNumber: `${data.phoneNumber}`,
            postcode: data.postcode
        });
        if(stepMax == 3)
        {
          setIsSubmitted(true);
        }
        else
          setStep((prev) => (prev + 1) as WizardStep);
      };

      if (
        emailValidationResult &&
        emailValidationResult.emailAddress === data.emailAddress &&
        emailValidationResult.isValid
      ) {
        goToNextStep();
      } else {
        validateEmail(data.emailAddress).then((r) => {
          if (r.success && r.payload && r.payload.emailAddress === data.emailAddress && r.payload.isValid) {
            goToNextStep();
          } else {
            setEmailValidationResult(r.success ? { ...r.payload } : null);
          }
        });
      }
    },
    [emailValidationResult, setPersonalInformation, setStep],
  );

  useEffect(() => {
    if (postAppointmentResponse) {
      window.location.href = postAppointmentResponse.retUrl;
    }
  }, [postAppointmentResponse]);

  const [day, month, year, emailAddress] = watch(['day', 'month', 'year', 'emailAddress']);

  React.useEffect(() => {
    if (day && day.length === 2 && +day <= 31 && +day >= 1) {
      setFocus('month');
    } else if (day && day.length > 2) {
      setValue('day', day.substring(0, 2));
      setFocus('month');
    }
  }, [day, setFocus, setValue]);

  React.useEffect(() => {
    if (month && month.length === 2 && +month <= 12 && +month >= 1) {
      setFocus('year');
    } else if (month && month.length > 2) {
      setValue('month', month.substring(0, 2));
      setFocus('year');
    }
  }, [month, setFocus, setValue]);

  React.useEffect(() => {
    if (year && year.length === 4 && +year <= new Date().getFullYear() && +year >= 1900) {
      setFocus('phoneNumber');
    } else if (year && year.length > 4) {
      setValue('year', year.substring(0, 4));
      setFocus('phoneNumber');
    }
  }, [year, setFocus, setValue]);

  const { onBlur: emailOnBlur, ...emailProps } = register('emailAddress', {
    pattern: emailPattern,
    required: true,
  });

  //preset note field in case eBooker is called by callcenter (and not enduser)?
  React.useEffect(() => {
    const isCallcenter = (window as any).isCallcenter; //set in Kentico page
    const isCallcenterTxt = isCallcenter ? intl.formatMessage({ id: 'isCallcenter' }) : '' ; //comment field default text, depending on if the callcenter runs teh eBooker
    setValue('note', isCallcenterTxt);
  });

  React.useEffect(() => {
    if (postAppointmentResponse) {
      window.location.href = postAppointmentResponse.retUrl;
    }
  }, [postAppointmentResponse]);

  return (
    <div className={classes.container}>
      <h2>{intl.formatMessage({ id: 'personalData.title' })}</h2>
      <form className={classes.wrapper} onSubmit={handleSubmit(onSubmit)}>
        <div className={classes.form}>
          <label className={classes.requiredField} htmlFor='appointmentOnBehalfOf'>
            {intl.formatMessage({ id: 'personalData.onBehalfOf' })}
          </label>

          <div className={classes.radioToolbar}>
            <input
              type='radio'
              id='radioMyself'
              defaultValue='myself'
              {...register('appointmentOnBehalfOf', { required: true })}
            />
            <label htmlFor='radioMyself'>
              <span>{intl.formatMessage({ id: 'personalData.myself' })}</span>
            </label>
            <input
              type='radio'
              id='radioFamily'
              defaultValue='familyMember'
              {...register('appointmentOnBehalfOf', { required: true })}
            />
            <label htmlFor='radioFamily'>
              <span>{intl.formatMessage({ id: 'personalData.family' })}</span>
            </label>
            <input
              type='radio'
              id='radioFriend'
              defaultValue='friend'
              {...register('appointmentOnBehalfOf', { required: true })}
            />
            <label htmlFor='radioFriend'>
              <span>{intl.formatMessage({ id: 'personalData.friend' })}</span>
            </label>
          </div>

          <div className={classes.onePersonHint}><strong>{intl.formatMessage({ id: 'personalData.onePersonHint1' })}</strong> {intl.formatMessage({ id: 'personalData.onePersonHint2' })}</div>

          <label className={classes.requiredField} htmlFor='gender'>
            {intl.formatMessage({ id: 'personalData.gender' })}
          </label>
          <div className={classes.radioToolbar}>
            <input type='radio' id='radioFemale' defaultValue='f' {...register('gender', { required: true })} />
            <label htmlFor='radioFemale'>
              <span>{intl.formatMessage({ id: 'personalData.female' })}</span>
            </label>
            <input type='radio' id='radioMale' defaultValue='m' {...register('gender', { required: true })} />
            <label htmlFor='radioMale'>
              <span>{intl.formatMessage({ id: 'personalData.male' })}</span>
            </label>
            <input type='radio' id='radioOther' defaultValue='o' {...register('gender', { required: true })} />
            <label htmlFor='radioOther'>
              <span>{intl.formatMessage({ id: 'personalData.other' })}</span>
            </label>
          </div>
          {errors.gender && (
            <span className={classes.required}>{intl.formatMessage({ id: 'personalData.genderError' })}</span>
          )}
          <div className={classes.names}>
            <label htmlFor='name'>
              <input
                id='name'
                placeholder={intl.formatMessage({ id: 'personalData.firstName' })}
                className={classes.input}
                type='text'
                {...register('name', { required: true })}
              ></input>
              <span className={classes.requiredField}>{intl.formatMessage({ id: 'personalData.firstName' })}</span>
            </label>

            <label htmlFor='lastname'>
              <input
                id='lastname'
                placeholder={intl.formatMessage({ id: 'personalData.lastName' })}
                className={classes.input}
                type='text'
                {...register('lastName', { required: true })}
              ></input>
              <span className={classes.requiredField}>{intl.formatMessage({ id: 'personalData.lastName' })}</span>
            </label>
          </div>
                  {(errors.name || errors.lastName) && (
                      <span className={classes.required}>
                          {errors.name?(intl.formatMessage({ id: 'personalData.forenameError' })):''}
                          {errors.name?(<br/>):''}
                          {errors.lastName?(intl.formatMessage({ id: 'personalData.nameError' })):''}
                      </span>
                  )}

                  <div className={classes.names}>
                      <label htmlFor='postcode'>
                          <input
                              id='postcode'
                              maxLength={6}
                              placeholder={intl.formatMessage({ id: 'personalData.postcode' })}
                              className={classes.input}
                              type='text'
                              {...register('postcode', { required: true })}
                          ></input>
                          <span className={classes.requiredField}>{intl.formatMessage({ id: 'personalData.postcode' })}</span>
                      </label>

                      <label htmlFor='houseNumber'>
                          <input
                              id='houseNumber'
                              maxLength={5}
                              placeholder={intl.formatMessage({ id: 'personalData.houseNumber' })}
                              className={classes.input}
                              type='text'
                              {...register('houseNumber', { required: true })}
                          ></input>
                          <span className={classes.requiredField}>{intl.formatMessage({ id: 'personalData.houseNumber' })}</span>
                      </label>

                  </div>
                  {(errors.postcode || errors.houseNumber) && (
                      <span className={classes.required}>
                          {errors.postcode?(intl.formatMessage({ id: 'personalData.postcodeError' })):''}
                          {errors.postcode?(<br/>):''}
                          {errors.houseNumber?(intl.formatMessage({ id: 'personalData.houseNumberError' })):''}
                      </span>
                  )}

          <label className={classes.requiredField} htmlFor='email'>
            {intl.formatMessage({ id: 'personalData.email' })}
          </label>
          <input
            id='email'
            placeholder={intl.formatMessage({ id: 'personalData.email' })}
            className={classes.input}
            type='text'
            onBlur={(e) => {
              setEmailValidationResult(null);
              if (emailPattern.test(e.target.value)) {
                validateEmail(e.target.value).then((r) => {
                  setEmailValidationResult(r.success ? { ...r.payload } : null);
                });
              }
              return emailOnBlur(e);
            }}
            {...emailProps}
          ></input>
          {errors.emailAddress && (
            <span className={classes.required}>{intl.formatMessage({ id: 'personalData.emailError' })}</span>
          )}
          {!errors.emailAddress &&
          emailValidationResult &&
          emailValidationResult.emailAddress === emailAddress &&
          !emailValidationResult.isValid ? (
            <span className={classes.requiredNoMarginBottom}>{intl.formatMessage({ id: 'personalData.emailErrorAdvanced' })}</span>
          ) : null}
          {!errors.emailAddress &&
          emailValidationResult &&
          emailValidationResult.emailAddress === emailAddress &&
          !emailValidationResult.isValid &&
          emailValidationResult.suggestion ? (
            <span className={classes.required}>
              {intl.formatMessage({ id: 'personalData.emailSuggestion' })} {emailValidationResult.suggestion}
            </span>
          ) : null}
          <label className={classes.requiredField} htmlFor='dateOfBirth'>
            {intl.formatMessage({ id: 'personalData.dateOfBirth' })}
          </label>
          <div className={classes.birth}>
            <input
              type='number'
              id='dateofbirth'
              min='1'
              max='31'
              placeholder={intl.formatMessage({ id: 'personalData.day' })}
              {...register('day', { max: 31, min: 1, required: true })}
            />
            <input
              type='number'
              id='dateofbirth'
              min='1'
              max='12'
              placeholder={intl.formatMessage({ id: 'personalData.month' })}
              {...register('month', { max: 12, min: 1, required: true })}
            />
            <input
              type='number'
              id='dateofbirth'
              min='1900'
              max={new Date().getFullYear()}
              placeholder={intl.formatMessage({ id: 'personalData.year' })}
              {...register('year', { max: new Date().getFullYear(), min: 1900, required: true })}
            />
          </div>
          {errors.day ? (
            <span className={classes.required}>{intl.formatMessage({ id: 'personalData.dateOfBirthError' })}</span>
          ) : errors.month ? (
            <span className={classes.required}>{intl.formatMessage({ id: 'personalData.dateOfBirthError' })}</span>
          ) : errors.year ? (
            <span className={classes.required}>{intl.formatMessage({ id: 'personalData.dateOfBirthError' })}</span>
          ) : null}

          {validateBirtdate(day, month, year) === false && !errors.day && !errors.month && ! errors.year ? <span className={classes.required}>{intl.formatMessage({ id: 'personalData.dateOfBirthError' })}</span>: null}

          <label htmlFor='phoneNumber'>{intl.formatMessage({ id: 'personalData.phone' })}</label>
          <input
            id='phoneNumber'
            // placeholder='+31 00 000 00 00'
            className={classes.input}
            type='text'
            {...register('phoneNumber', {
              pattern: /^[0-9]{10}$/,
            })}
          />
          {errors.phoneNumber && (
            <span className={classes.required}>{intl.formatMessage({ id: 'personalData.phoneError' })}</span>
          )}

          <label htmlFor='note'>{intl.formatMessage({ id: 'personalData.note' })}</label>
          <textarea
            rows={5}
            id='note'
            placeholder={intl.formatMessage({ id: 'personalData.textfield' })}
            className={classes.input}
            {...register('note')}
          />

          <div className={classes.agreements}>
            <label className='checkboxContainer' htmlFor='agreementMarketing'>
              <input
                id='agreementMarketing'
                className={classes.input}
                type='checkbox'
                {...register('agreementMarketing')}
              ></input>
              <span className='checkmark'></span>
              {intl.formatMessage({
                defaultMessage: 'privacy policy text',
                id: 'personalData.agreementMarketing',
              })}
            </label>
          </div>

          <div className={classes.policy}>
            {intl.formatMessage({
              defaultMessage: 'privacy policy text',
              id: 'personalData.agreementGeneral',
            })}
            <a
              className={classes.agreementLink}
              href={intl.formatMessage({
                id: 'personalData.agreementMarketingGeneralLink',
              })}
              target='_blank'
              rel='noreferrer'
            >
              {' '}
              {intl.formatMessage({
                id: 'personalData.agreementMarketingGeneralLinkText',
              })}
            </a>
          </div>

          {!slotAvailability && stepMax == 3 && (
            <div className={classes.timeslotVerification}>
              <span>{intl.formatMessage({ id: 'summary.timeslotVerificationDescription' })}</span>
            </div>
          )}

          {!slotAvailability && stepMax == 3 && (
            <button type='button' className={classes.button} onClick={() => setStep(2)}>
              {intl.formatMessage({ id: 'summary.returnToTimeslots' })}
            </button>
          )}


          <button
            type='submit'
            className={classes.button}
            disabled={!isValid || Boolean(emailValidationResult && !emailValidationResult.isValid) || !validateBirtdate(day, month, year)}
          >
            { stepMax != 3 && intl.formatMessage({ id: 'personalData.confirm' })}

            { stepMax == 3 && isSubmitted && <Icon path={mdiLoading} size='16px' className={classes.loadingButtonSpinner} />}{' '}
            { stepMax == 3 && intl.formatMessage({ id: 'personalData.confirm.v2b' })}

          </button>
        </div>
        <div className={classes.side}>
          <div>
            <b>{intl.formatMessage({ id: 'personalData.address' })}</b><br/>
            <p className={classes.minimalMarginBottom}>{ctx.store?.name}</p>
            <p className={classes.minimalMarginBottom}>{ctx.store?.street}</p>
            <p className={classes.minimalMarginBottom}>{ctx.store?.postalCode} {ctx.store?.city}</p>
            <p>
              <br />
              <b>{intl.formatMessage({ defaultMessage: 'Afspraak', id: 'personalData.dateName' })}</b><br />
              {formatSelectedSlotDateOnly(intl.locale, new Date(ctx.storeSlot?.startDateTime || ''))}<br />
              {intl.formatMessage({ id: 'personalData.time' })} {formatSelectedSlotTimeOnly(intl.locale, new Date(ctx.storeSlot?.startDateTime || ''))} {intl.formatMessage({ id: 'personalData.oclock' })}
            </p>
          </div>
          <button type='button' className={classes.moreInfoButton} onClick={() => setExtraInfoOpen((prev) => !prev)}>
            Meer Info
          </button>
          <div className={clsx(classes.sideExtraInfo, extraInfoOpen ? classes.isExtraOpen : null)}>
            <div className={classes.sidedivider}></div>
            <div className={classes.sideContentWrapper}>
              <div dangerouslySetInnerHTML={{ __html: intl.formatMessage({ id: 'sideColumn' }) }} />
              {/* <div>
                <h4>
                  <svg width='28' height='28' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <circle cx='14' cy='14' r='14' fill='#95C11F' />
                    <path d='M9 14.157L12.6667 17.8237L20 10.4904' stroke='white' strokeWidth='2' />
                  </svg>
                  <b>lorem ipsum</b>
                </h4>
                <p>
                  Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the
                  industry standard dummy text ever since the 1500s, when an unknown printer took a galley of type and
                  scrambled it to make a type specimen book.
                </p>
              </div>
              <div>
                <h4>
                  <svg width='28' height='28' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <circle cx='14' cy='14' r='14' fill='#95C11F' />
                    <path d='M9 14.157L12.6667 17.8237L20 10.4904' stroke='white' strokeWidth='2' />
                  </svg>
                  <b>lorem ipsum</b>
                </h4>
                <p>
                  Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the
                  industry standard dummy text ever since the 1500s, when an unknown printer took a galley of type and
                  scrambled it to make a type specimen book.
                </p>
              </div>
              <div>
                <h4>
                  <svg width='28' height='28' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <circle cx='14' cy='14' r='14' fill='#95C11F' />
                    <path d='M9 14.157L12.6667 17.8237L20 10.4904' stroke='white' strokeWidth='2' />
                  </svg>
                  <b>lorem ipsum</b>
                </h4>
                <p>
                  Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the
                  industry standard dummy text ever since the 1500s, when an unknown printer took a galley of type and
                  scrambled it to make a type specimen book.
                </p>
              </div> */}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PersonalDataInput;
